import { makeStyles, Text, Title3 } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import SEO from "../../app/SEO";
import ITitle from "../../models/ITitle";
import Hero from "../../components/hero/Hero";
import { dolphinTokens } from "../../utils/Theme";
import Map from "../../components/map/Map";
import Form from "../../components/contactForm/Form";
import IAddress from "../../models/localisation/IAddress";
import Status from "../../enums/Status";
import addressService from "../../services/AddressService";
import { formatAddressMultiline, formatAddressSingleLine } from "../../utils/Address";
import { useHeroMainSnap } from "../../hooks/useHeroMainSnap";

const useStyles = makeStyles({
    mainContainer: {
        padding: 0,
        backgroundColor: '#f5f5f5',
    },
    detailsContainer: {
        backgroundColor: dolphinTokens.colorLightBlue80,
        color: "white",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",

        "@media (max-width: 600px)": {
            flexDirection: "column",
            alignItems: "stretch"
        }
    },
    detail: {
        width: "100%",
        padding: "20px 0"
    },
    address: {
        whiteSpace: "pre-line",
    },
    mapContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",

        "@media (max-width: 600px)": {
            flexDirection: "column",
            paddingTop: "16px",
        }
    },
    mapPlaceholder: {
        width: "40%",
        height: "400px"
    },
    form: {
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        paddingBottom: "16px",

        "@media (min-width: 600px)": {
            padding: "16px"
        }
    },
    link: {
        textDecoration: "none",
        color: "inherit",
    }
});

export default function ContactPage() {
    const { t } = useTranslation(["contact", "general"]);
    const styles = useStyles();

    const [address, setAddress] = useState<IAddress>(null!);
    const [status, setStatus] = useState<Status>(Status.LOADING);

    useEffect(() => {
        fetchAddress(setAddress, setStatus);
    }, []);

    useHeroMainSnap("contact-hero", "contact-main", {
        duration: 900,
        easing: 'quart',
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    const title: ITitle = {
        title: t("titles.fullContactUs"),
        pageTitle: t("titles.fullContactUs"),
    };

    const metaDescription = t("website.metaDescription", { ns: "general" });
    const bannerImageUrl = "/img/banners/contact.webp";

    const addressText = status === Status.LOADING
        ? t("loading", { ns: "general" })
        : status === Status.LOAD_SUCCESS && !!address
            ? formatAddressSingleLine(address)
            : t("details.address.error");

    const mapAddressText = status === Status.LOAD_SUCCESS && !!address
        ? formatAddressMultiline(address)
        : "";

    return (
        <div className={styles.mainContainer}>

            <SEO
                title={title}
                metaDescription={metaDescription}
                canonicalPath={`/contact`}
                openGraph={{
                    title: title.title,
                    description: metaDescription,
                    image: bannerImageUrl,
                    url: "contact"
                }}
            />

            <div id="contact-hero">
                <Hero
                    title={t('titles.contactUs')}
                    subtitle={t('subtitle')}
                    imageUri={bannerImageUrl}
                />
            </div>

            <div id="contact-main">

                <div className={styles.detailsContainer}>
                    
                    <div className={styles.detail}>
                        <Title3 block as="h3" align="center">
                            {t("headers.address")}
                        </Title3>
                        <Text block as="p" align="center" className={styles.address}>
                            {addressText}
                        </Text>
                    </div>

                    <div className={styles.detail}>
                        <Title3 block as="h3" align="center">
                            {t("headers.phone")}
                        </Title3>
                        <Text block as="p" align="center">
                            {
                                status === Status.LOADING
                                    ? t("loading", { ns: "general" })
                                    : status === Status.LOAD_SUCCESS && address?.phone
                                        ?   <a href={`tel:${address.phone}`} className={styles.link}>
                                                {address.phone}
                                            </a>
                                        :   t("details.phone.noPhone")
                            }
                        </Text>
                    </div>

                    <div className={styles.detail}>
                        <Title3 block as="h3" align="center">
                            {t("headers.email")}
                        </Title3>
                        <Text block as="p" align="center">
                            <a href={`mailto:${t("details.email.value")}`} className={styles.link}>
                                {t("details.email.value")}
                            </a>
                        </Text>
                    </div>

                </div>

                <div className={styles.mapContainer}>
                    {
                        status === Status.LOADING || status === Status.LOAD_ERROR
                            ?   <div className={styles.mapPlaceholder} />
                            :   <Map
                                    coordinates={[address.lat, address.long]}
                                    zoom={16}
                                    marker={{
                                        color: dolphinTokens.colorLightBlue,
                                        position: [address.lat, address.long]
                                    }}
                                    popup={
                                        !!mapAddressText
                                            ? {
                                                content: mapAddressText,
                                                closeButton: true,
                                            }
                                            : undefined
                                    }
                                    isRounded={false}
                                    />
                    }

                    <div className={styles.form}>
                        <Title3 block as="h3" align="center">
                            {t("form.title")}
                        </Title3>

                        <Form />
                    </div>
                </div>

            </div>

        </div>
    );
}

async function fetchAddress (
    setAddress: (address: IAddress) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const address = await addressService.getLocalAddress();
        setAddress(address);
        setStatus(Status.LOAD_SUCCESS);
    } catch (error) {
        setStatus(Status.LOAD_ERROR);
    }
}
